import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Lock, X, Maximize2, Download } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS_STYLES = {
  open: { color: "#00E5FF", label: "open · awaiting admin reply" },
  pending: { color: "#FFB000", label: "pending · awaiting your reply" },
  closed: { color: "#888", label: "closed · read-only" },
};

/**
 * Reply-to-Review modal. Manages a single ticket per review.
 * Opens on demand and lazy-loads / lazy-creates the ticket.
 */
export default function TicketModal({ review, open, onClose, onTicketChanged }) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [enlarged, setEnlarged] = useState(null);
  const endRef = useRef(null);

  const openResponseImage = async (img) => {
    if (!review) return;
    try {
      const { data } = await http.get(`/expert-review/${review.id}/response-images/${img.id}`);
      setEnlarged({
        src: `data:${data.image_mime};base64,${data.image_base64}`,
        filename: data.filename || `chartlenz-review-${review.id.slice(0, 8)}.${(data.image_mime || "image/png").split("/")[1]}`,
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    if (!open || !review) return;
    let alive = true;
    setLoading(true);
    setTicket(null);
    (async () => {
      try {
        const { data } = await http.get(`/tickets/by-review/${review.id}`);
        if (alive) setTicket(data || null);
      } catch { /* ignore */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [open, review]);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
  }, [ticket?.messages?.length]);

  const closed = ticket?.status === "closed";
  const userLocked = ticket?.user_locked || closed;

  const send = async () => {
    const msg = draft.trim();
    if (!msg) return;
    setSending(true);
    try {
      let updated;
      if (!ticket) {
        const { data } = await http.post("/tickets", { review_id: review.id, message: msg });
        updated = data;
      } else {
        const { data } = await http.post(`/tickets/${ticket.id}/messages`, { message: msg });
        updated = data;
      }
      setTicket(updated);
      setDraft("");
      onTicketChanged && onTicketChanged(updated);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSending(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="ticket-modal"
        >
          <motion.div
            className="bg-neutral-950 border border-neutral-800 w-full max-w-2xl max-h-[90vh] flex flex-col"
            initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-900">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500">
                  ── follow-up ──
                </div>
                <div className="font-bold text-white mt-0.5">
                  {review.asset} · Review Discussion
                </div>
              </div>
              <button onClick={onClose} data-testid="ticket-close" className="text-neutral-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Status pill */}
            {ticket && (
              <div className="px-5 py-2 border-b border-neutral-900 flex items-center justify-between" data-testid="ticket-status">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em]"
                     style={{ color: STATUS_STYLES[ticket.status]?.color }}>
                  {STATUS_STYLES[ticket.status]?.label || ticket.status}
                </div>
                <div className="text-[10px] font-mono text-neutral-600 uppercase tracking-[0.2em]">
                  {(ticket.messages || []).length} msg
                </div>
              </div>
            )}

            {/* Response images attached to the review (visible inside the ticket too) */}
            {(review?.response_images || []).length > 0 && (
              <div className="px-5 py-3 border-b border-neutral-900" data-testid="ticket-response-images">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 mb-2">
                  attached screenshots from review
                </div>
                <div className="flex flex-wrap gap-2">
                  {review.response_images.map((img) => (
                    <button
                      key={img.id}
                      onClick={() => openResponseImage(img)}
                      data-testid={`ticket-view-image-${img.id}`}
                      className="group relative w-24 h-16 border border-neutral-800 hover:border-[#00E5FF] bg-black overflow-hidden transition-colors"
                      title={img.filename || "View"}
                    >
                      <div className="absolute inset-0 flex items-center justify-center text-neutral-500 group-hover:text-[#00E5FF]">
                        <Maximize2 className="w-4 h-4" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="ticket-messages">
              {loading && (
                <div className="text-xs font-mono text-neutral-600 uppercase tracking-[0.25em]">loading…</div>
              )}
              {!loading && !ticket && (
                <div className="text-sm text-neutral-400">
                  Send your first follow-up question about <span className="text-white font-bold">{review.asset}</span>.
                  Your trader will reply here.
                </div>
              )}
              {(ticket?.messages || []).map((m) => (
                <div
                  key={m.id}
                  className={`max-w-[85%] ${m.author === "user" ? "ml-auto" : ""}`}
                >
                  <div
                    className={`px-4 py-2.5 text-sm leading-relaxed whitespace-pre-line border ${
                      m.author === "user"
                        ? "bg-[#00E5FF]/[0.06] border-[#00E5FF]/30 text-white"
                        : "bg-neutral-900 border-neutral-800 text-neutral-100"
                    }`}
                  >
                    {m.body}
                  </div>
                  <div className={`mt-1 text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-600 ${
                    m.author === "user" ? "text-right" : ""
                  }`}>
                    {m.author === "user" ? "you" : "ChartLenz trader"} · {new Date(m.created_at).toLocaleString()}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>

            {/* Composer */}
            <div className="border-t border-neutral-900 p-3">
              {closed ? (
                <div className="flex items-center gap-2 text-xs text-neutral-500" data-testid="ticket-closed-notice">
                  <Lock className="w-3.5 h-3.5" /> This ticket is closed. You can request a new Expert Review for a follow-up.
                </div>
              ) : userLocked ? (
                <div className="flex items-center gap-2 text-xs text-neutral-500" data-testid="ticket-userlocked-notice">
                  <Lock className="w-3.5 h-3.5" /> You've used your follow-up reply. Your trader will respond here — you'll see new replies as they come in.
                </div>
              ) : (
                <div className="flex items-end gap-2">
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="One follow-up question…"
                    rows={2}
                    data-testid="ticket-input"
                    className="flex-1 bg-black border border-neutral-800 px-3 py-2 text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-[#00E5FF] resize-none"
                  />
                  <button
                    onClick={send}
                    disabled={sending || !draft.trim()}
                    data-testid="ticket-send"
                    className="inline-flex items-center gap-1.5 bg-white text-black font-bold text-xs uppercase tracking-wider px-4 py-3 hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Send className="w-3.5 h-3.5" /> Send
                  </button>
                </div>
              )}
              {!closed && !userLocked && (
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-600 mt-2">
                  one reply allowed · admin can respond unlimited times
                </div>
              )}
            </div>
          </motion.div>

          {/* Lightbox for response images opened from inside the ticket */}
          {enlarged && (
            <div
              className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4 cursor-zoom-out"
              onClick={(e) => { e.stopPropagation(); setEnlarged(null); }}
              data-testid="ticket-image-lightbox"
            >
              <div className="absolute top-4 right-4 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <a
                  href={enlarged.src}
                  download={enlarged.filename}
                  data-testid="ticket-lightbox-download"
                  className="inline-flex items-center gap-1.5 bg-white text-black font-mono text-xs uppercase tracking-[0.2em] px-3 py-2 hover:bg-neutral-200"
                >
                  <Download className="w-3.5 h-3.5" /> download
                </a>
                <button onClick={() => setEnlarged(null)} className="text-white p-2" aria-label="close">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <img src={enlarged.src} alt={enlarged.filename} className="max-w-full max-h-full object-contain border border-neutral-800" />
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
